import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import useRequestBadges from '../../../hooks/features/common/useRequestBadges';
import {
  formatPriorityLabel,
  getPriorityBadgeClassName,
} from '../../../utils/priorityBadgeUtils';
import { REQUEST_STATUS } from '../../../constants/request.constant';
import '../../../styles/priorityBadge.css';
import styles from './RequestBadges.module.css';

/**
 * RequestBadges Component
 *
 * Displays request metadata badges: Priority (SLA only), Request ID, and Remaining Days (SLA only).
 * For completed requests, shows Approved/Rejected status instead of priority and SLA.
 *
 * @param {Object} props
 * @param {string|number} props.requestProgressId - For fetching priority data
 * @param {string} [props.fallbackRequestId] - Display id when API omits requestId
 * @param {string|null} [props.requestOutcomeStatus] - REQUEST_STATUS.APPROVED or REQUEST_STATUS.REJECTED
 */
const RequestBadges = ({
  requestProgressId,
  fallbackRequestId,
  requestOutcomeStatus = null,
}) => {
  const { t } = useTranslation();

  const isCompletedRequest =
    requestOutcomeStatus === REQUEST_STATUS.APPROVED ||
    requestOutcomeStatus === REQUEST_STATUS.REJECTED;

  const { priority, displayDays, isVisible, showDays, requestId, slaEnabled } =
    useRequestBadges({
      requestProgressId,
      skipFetch: isCompletedRequest,
    });

  const priorityClassName = useMemo(
    () => getPriorityBadgeClassName(priority || 'normal'),
    [priority]
  );

  const displayRequestId = requestId || fallbackRequestId;

  const statusBadge = useMemo(() => {
    if (requestOutcomeStatus === REQUEST_STATUS.APPROVED) {
      return { text: t('Approved'), className: styles.statusApproved };
    }
    if (requestOutcomeStatus === REQUEST_STATUS.REJECTED) {
      return { text: t('Rejected'), className: styles.statusRejected };
    }
    return null;
  }, [requestOutcomeStatus, t]);

  if (!isCompletedRequest && !isVisible) {
    return null;
  }

  if (isCompletedRequest && !statusBadge && !displayRequestId) {
    return null;
  }

  return (
    <div className={styles.badgeContainer}>
      {statusBadge && (
        <span className={`${styles.statusBadge} ${statusBadge.className}`}>
          {statusBadge.text}
        </span>
      )}

      {!isCompletedRequest && slaEnabled && priority && (
        <span className={priorityClassName}>
          {t('requestBadges.priority', 'Priority')}:{' '}
          {formatPriorityLabel(priority)}
        </span>
      )}

      {displayRequestId && (
        <span className={styles.badge}>#{displayRequestId}</span>
      )}

      {!isCompletedRequest && slaEnabled && showDays && (
        <span className={`${styles.badge} ${styles.daysBadge}`}>
          {displayDays} {t('requestBadges.days', 'Days')}
        </span>
      )}
    </div>
  );
};

export default RequestBadges;
